import { siteSettings } from "../static";
import axios from "axios";

const API_BASE_URL = siteSettings.cryptoServerLink;

// Object to store fallback prices (only when backend is completely down)
let emergencyPrices = {
  USD: 1,
};

// CoinRanking fallback configuration (copied from oldService.js)
const COINRANKING_FALLBACK = {
  baseUrl: "https://coinranking1.p.rapidapi.com/coins",
  params: {
    referenceCurrencyUuid: "yhjMzLPhuIDl",
    timePeriod: "24h",
    orderBy: "marketCap",
    offset: "0",
    limit: "1000",
  },
  headers: {
    "x-rapidapi-key": "396a8cb761mshc0459779f675ee6p18d42djsn4cd87cfd13f7",
    "x-rapidapi-host": "coinranking1.p.rapidapi.com",
  },
};

// API Client Configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * CoinRanking fallback function (copied from oldService.js)
 */
async function fetchCoinRankingFallback() {
  try {
    const response = await axios.get(COINRANKING_FALLBACK.baseUrl, {
      headers: COINRANKING_FALLBACK.headers,
      params: COINRANKING_FALLBACK.params,
    });

    console.log("CoinRanking response:", response.data); // Debug log

    return Object.fromEntries(
      response.data.data.coins.map((item) => [
        item.symbol,
        Number.parseFloat(item.price),
      ]),
    );
  } catch (error) {
    console.warn("⚠️ CoinRanking fallback also failed:", error.message);
    console.warn("CoinRanking error details:", error.response?.data); // More detailed error info
    return {};
  }
}

/**
 * Fetch crypto prices from backend with CoinRanking fallback
 */
async function fetchCryptoPrices(symbols = null) {
  try {
    const params = symbols ? { symbols: symbols.join(",") } : {};
    const response = await apiClient.get("/prices/crypto", { params });

    // Backend returns { success: true, data: {...}, timestamp: ... }
    const prices = response.data.data || {};
    console.log(prices);

    // Check if prices are valid (not NaN or null)
    const hasValidPrices = Object.values(prices).some(
      (price) =>
        price !== null && price !== undefined && !isNaN(price) && price > 0,
    );

    if (hasValidPrices) {
      // console.log(
      //   `✅ Crypto: Got ${Object.keys(prices).length} prices from backend`
      // );
      return prices;
    } else {
      console.warn(
        "⚠️ Backend returned invalid prices (NaN/null), trying fallback",
      );

      // Use fallback directly when invalid prices detected
      const fallbackPrices = await fetchCoinRankingFallback();
      if (Object.keys(fallbackPrices).length > 0) {
        console.log(
          `🔄 Using CoinRanking fallback: Got ${
            Object.keys(fallbackPrices).length
          } prices`,
        );
        return fallbackPrices;
      }
      return {};
    }
  } catch (error) {
    console.warn(
      "⚠️ Backend crypto API failed, trying CoinRanking fallback:",
      error.message,
    );

    // Try CoinRanking fallback
    const fallbackPrices = await fetchCoinRankingFallback();
    if (Object.keys(fallbackPrices).length > 0) {
      console.log(
        `🔄 Using CoinRanking fallback: Got ${
          Object.keys(fallbackPrices).length
        } prices`,
      );
      return fallbackPrices;
    }

    return {};
  }
}

/**
 * Fetch stock prices from backend
 */
async function fetchStockPrices(symbols = null) {
  try {
    const params = symbols ? { symbols: symbols.join(",") } : {};
    const response = await apiClient.get("/prices/stocks", { params });

    // Backend returns { success: true, data: {...}, timestamp: ... }
    const prices = response.data.data || {};
    // console.log(
    //   `✅ Stocks: Got ${Object.keys(prices).length} prices from backend`
    // );
    return prices;
  } catch (error) {
    console.warn("⚠️ Backend stocks API failed:", error.message);
    return {};
  }
}

/**
 * Fetch currency rates from backend
 */
async function fetchCurrencyRates(symbols = null) {
  try {
    const params = symbols ? { symbols: symbols.join(",") } : {};
    const response = await apiClient.get("/prices/currencies", { params });

    const rates = response.data.data || {};
    // console.log(
    //   `✅ Currencies: Got ${Object.keys(rates).length} rates from backend`
    // );
    return rates;
  } catch (error) {
    console.warn("⚠️ Backend currency API failed:", error.message);
    return {};
  }
}

/**
 * Main function to get all prices (drop-in replacement for old getAllPrices)
 */
export async function getAllPrices() {
  try {
    // console.log("🚀 Fetching all prices from backend...");

    // Get all prices (crypto + stocks + currencies) - simple and reliable
    const [cryptoPrices, stockPrices, currencyRates] = await Promise.all([
      fetchCryptoPrices(),
      fetchStockPrices(),
      fetchCurrencyRates(),
    ]);

    // Combine all prices (just like your old code)
    const allPrices = {
      ...cryptoPrices,
      ...stockPrices,
      ...currencyRates,
    };

    // Apply emergency fallbacks only if completely empty
    if (Object.keys(allPrices).length === 0) {
      console.warn("⚠️ Backend completely failed, using emergency prices");
      return { ...emergencyPrices };
    }

    // console.log(`📊 Total prices fetched: ${Object.keys(allPrices).length}`);
    return allPrices;
  } catch (error) {
    console.error("❌ Critical error in getAllPrices:", error);

    // Return emergency prices if everything fails
    console.warn("⚠️ Using emergency fallback prices");
    return { ...emergencyPrices };
  }
}

// Export default for backward compatibility
export default {
  getAllPrices,
};
